import {
  useEffect, useMemo, useRef, useState,
} from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Typography from '@material-ui/core/Typography'
import {
  BookOpen,
  Calendar,
  Clock,
  MapPinned,
  COACH_PROFILE_FIELD_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import { gtmEvent } from '@lib/gtm-utils'
import { useTrackCoachProfileEvent } from '@lib/pga-coach/coach-profile-analytics'
import { useBookingAvailabilityByLessonType } from './coach-profile-booking-widget.availability'
import {
  AVAILABLE_SLOT_LIMIT,
  BOOKING_WIDGET_HEADING,
  BOOKING_WIDGET_HEADING_ID,
  BOOKING_WIDGET_TEST_ID,
  DEFAULT_PREFERRED_MINUTES,
  DEFAULT_TIMEZONE,
  FULL_CALENDAR_LABEL,
  NO_TIMES_LABEL,
  PREFERRED_TIME_OPTIONS,
  SELECT_A_TIME_LABEL,
  VIEW_FULL_AVAILABILITY_LABEL,
  buildBookingLocationOptions,
  buildDateOptionsFromSlots,
  buildPrivateLessonScheduleUrl,
  buildScheduleCalendarStartDateTime,
  buildViewFullAvailabilityScheduleUrl,
  collectGraphqlFacilities,
  facilityTimezoneFromLessonTypes,
  filterSlotsByLocationId,
  filterSlotsOnDate,
  findEarliestBookableSlot,
  formatBookingDateLabel,
  formatBookingDateShortLabel,
  formatBookingOfferingLabel,
  formatSlotTimeLabel,
  getBookableOfferingsAtLocation,
  getBookablePrivateLessonOfferingRows,
  getCoachPrivateScheduleUrl,
  getOfferingBookingKey,
  getUniqueBookableLessonTypeIds,
  mergeAvailabilitySlotsByLessonType,
  offeringHasBookableSlotsAtLocation,
  offeringTimezone,
  pickDefaultOfferingAtLocation,
  pickSlotsNearPreferredTime,
  resolveNextAvailableFromSlots,
  resolveOfferingDefaults,
} from './coach-profile-booking-widget.utils'
import useStyles from './coach-profile-booking-widget.styles'

const MENU_PROPS = {
  elevation: 8,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: {
    style: {
      maxHeight: 320,
      backgroundColor: '#fff',
      borderRadius: 12,
      border: '1px solid #dfe3ea',
      marginTop: 4,
    },
  },
}

function renderTruncatedSelectLabel(styles, label) {
  const text = label ?? ''
  return (
    <span className={styles.selectValueText} title={text}>
      {text}
    </span>
  )
}

function CoachProfileBookingWidget({
  coach,
  offerings,
  sidebar = false,
}) {
  const styles = useStyles()
  const scheduleUrl = getCoachPrivateScheduleUrl(coach)
  const bookableRows = useMemo(
    () => getBookablePrivateLessonOfferingRows(offerings),
    [offerings],
  )
  const lessonTypeIds = useMemo(
    () => getUniqueBookableLessonTypeIds(bookableRows),
    [bookableRows],
  )
  const prefetchTimezone = offeringTimezone(bookableRows[0])

  const {
    loading: availabilityLoading,
    byLessonType: availabilityByLessonType,
    error: availabilityError,
  } = useBookingAvailabilityByLessonType({
    lessonTypeIds,
    scheduleUrl,
    timezone: prefetchTimezone,
  })

  const trackCoachProfileEvent = useTrackCoachProfileEvent()
  const [selectedLocationId, setSelectedLocationId] = useState('')
  const [selectedOfferingKey, setSelectedOfferingKey] = useState(
    () => getOfferingBookingKey(bookableRows[0] || {}),
  )
  const [selectedDate, setSelectedDate] = useState(() => (
    bookableRows[0]
      ? resolveOfferingDefaults(bookableRows[0]).dateIso
      : ''
  ))
  const [preferredMinutes, setPreferredMinutes] = useState(() => (
    bookableRows[0]
      ? resolveOfferingDefaults(bookableRows[0]).preferredMinutes
      : DEFAULT_PREFERRED_MINUTES
  ))

  const defaultsAppliedKeyRef = useRef(null)
  const prevActiveLocationIdRef = useRef('')
  const skipSelectionRefreshRef = useRef(true)
  const [selectionRefreshing, setSelectionRefreshing] = useState(false)
  const mergedSlots = useMemo(
    () => mergeAvailabilitySlotsByLessonType(availabilityByLessonType),
    [availabilityByLessonType],
  )
  const graphqlFacilities = useMemo(
    () => collectGraphqlFacilities(availabilityByLessonType),
    [availabilityByLessonType],
  )

  const locationOptions = useMemo(
    () => buildBookingLocationOptions({
      slots: mergedSlots,
      facilityOfferings: bookableRows,
      graphqlFacilities,
      timezone: prefetchTimezone,
      requireSlots: true,
    }),
    [bookableRows, graphqlFacilities, mergedSlots, prefetchTimezone],
  )

  const hasMultipleLocations = locationOptions.length > 1
  const activeLocationId = hasMultipleLocations
    ? (selectedLocationId || locationOptions[0]?.value || '')
    : (
      locationOptions[0]?.value
      || String(bookableRows[0]?.facilityId || '').trim()
    )

  const offeringsAtLocation = useMemo(
    () => getBookableOfferingsAtLocation(bookableRows, activeLocationId),
    [activeLocationId, bookableRows],
  )

  const selectedOffering = useMemo(
    () => offeringsAtLocation.find((row) => (
      getOfferingBookingKey(row) === selectedOfferingKey
    )) || offeringsAtLocation[0] || bookableRows[0],
    [bookableRows, offeringsAtLocation, selectedOfferingKey],
  )

  const selectedLessonTypePayload = availabilityByLessonType[
    selectedOffering?.lessonTypeId
  ]
  const timezone = (
    selectedOffering?.timezone
    || facilityTimezoneFromLessonTypes(
      selectedLessonTypePayload ? [selectedLessonTypePayload] : null,
      activeLocationId || selectedOffering?.facilityId,
    )
    || prefetchTimezone
    || DEFAULT_TIMEZONE
  )

  const horizonSlots = selectedLessonTypePayload?.availableTimes || []
  const slotsForSelection = useMemo(() => {
    if (!activeLocationId) return horizonSlots
    return filterSlotsByLocationId(horizonSlots, activeLocationId)
  }, [activeLocationId, horizonSlots])

  const hasBookableTimes = !availabilityLoading
    && slotsForSelection.length > 0
    && buildDateOptionsFromSlots(slotsForSelection, timezone).length > 0

  const dateOptions = useMemo(
    () => (hasBookableTimes
      ? buildDateOptionsFromSlots(slotsForSelection, timezone)
      : []),
    [hasBookableTimes, slotsForSelection, timezone],
  )

  useEffect(() => {
    if (availabilityLoading || !locationOptions.length) return
    if (selectedLocationId && locationOptions.some((option) => (
      option.value === selectedLocationId
    ))) {
      return
    }
    const earliest = findEarliestBookableSlot(mergedSlots, prefetchTimezone)
    const nextLocationId = String(earliest?.locationId || '').trim()
    const defaultLocation = locationOptions.find((option) => (
      option.value === nextLocationId
    )) || locationOptions[0]
    setSelectedLocationId(defaultLocation?.value || '')
  }, [
    availabilityLoading,
    locationOptions,
    mergedSlots,
    prefetchTimezone,
    selectedLocationId,
  ])

  useEffect(() => {
    if (availabilityLoading || !activeLocationId || !offeringsAtLocation.length) {
      return
    }
    const preferredKey = pickDefaultOfferingAtLocation({
      offeringsAtLocation,
      availabilityByLessonType,
      locationId: activeLocationId,
      timezone: prefetchTimezone,
    })
    if (!preferredKey) return

    const locationChanged = prevActiveLocationIdRef.current !== activeLocationId
    prevActiveLocationIdRef.current = activeLocationId

    if (locationChanged) {
      setSelectedOfferingKey(preferredKey)
      return
    }

    if (selectedOfferingKey === preferredKey) return

    const selectedRow = offeringsAtLocation.find((row) => (
      getOfferingBookingKey(row) === selectedOfferingKey
    ))
    const selectedHasSlots = selectedRow
      ? offeringHasBookableSlotsAtLocation({
        offering: selectedRow,
        availabilityByLessonType,
        locationId: activeLocationId,
        timezone: prefetchTimezone,
      })
      : false

    if (!selectedRow || !selectedHasSlots) {
      setSelectedOfferingKey(preferredKey)
    }
  }, [
    activeLocationId,
    availabilityByLessonType,
    availabilityLoading,
    offeringsAtLocation,
    prefetchTimezone,
    selectedOfferingKey,
  ])

  useEffect(() => {
    if (availabilityLoading) return undefined
    if (skipSelectionRefreshRef.current) {
      skipSelectionRefreshRef.current = false
      return undefined
    }
    setSelectionRefreshing(true)
    const timer = window.setTimeout(() => setSelectionRefreshing(false), 450)
    return () => window.clearTimeout(timer)
  }, [
    activeLocationId,
    availabilityLoading,
    preferredMinutes,
    selectedDate,
    selectedOfferingKey,
  ])

  const showAvailabilityProgress = availabilityLoading || selectionRefreshing

  useEffect(() => {
    defaultsAppliedKeyRef.current = null
  }, [activeLocationId, selectedOfferingKey])

  useEffect(() => {
    if (availabilityLoading || !selectedOffering?.lessonTypeId || !hasBookableTimes) {
      return
    }
    const defaultsKey = `${getOfferingBookingKey(selectedOffering)}:${activeLocationId || 'default'}`
    if (defaultsAppliedKeyRef.current === defaultsKey) return
    defaultsAppliedKeyRef.current = defaultsKey
    const defaults = resolveNextAvailableFromSlots(
      slotsForSelection,
      timezone,
    )
    setSelectedDate(defaults.dateIso)
    setPreferredMinutes(defaults.preferredMinutes)
  }, [
    activeLocationId,
    availabilityLoading,
    hasBookableTimes,
    selectedOffering,
    slotsForSelection,
    timezone,
  ])

  useEffect(() => {
    if (!dateOptions.length) return
    if (!dateOptions.some((option) => option.value === selectedDate)) {
      setSelectedDate(dateOptions[0].value)
    }
  }, [dateOptions, selectedDate])

  const nearbySlots = useMemo(() => {
    if (!hasBookableTimes) return []
    const onDate = filterSlotsOnDate(
      slotsForSelection,
      selectedDate,
      timezone,
    )
    return pickSlotsNearPreferredTime(
      onDate,
      preferredMinutes,
      timezone,
      AVAILABLE_SLOT_LIMIT,
    )
  }, [hasBookableTimes, preferredMinutes, selectedDate, slotsForSelection, timezone])

  const widgetRendered = Boolean(scheduleUrl) && bookableRows.length > 0
  const impressionTrackedRef = useRef(false)
  useEffect(() => {
    if (!widgetRendered || impressionTrackedRef.current) return
    impressionTrackedRef.current = true
    trackCoachProfileEvent('view-coach-profile-booking-widget', {
      bookable_offering_count: bookableRows.length,
      lesson_type_count: lessonTypeIds.length,
    })
  }, [bookableRows.length, lessonTypeIds.length, trackCoachProfileEvent, widgetRendered])

  if (!widgetRendered) return null

  const hasMultipleOfferingsAtLocation = offeringsAtLocation.length > 1
  const selectedOfferingLabel = formatBookingOfferingLabel(selectedOffering)
  const coachName = coach?.displayName
    || [coach?.firstName, coach?.lastName].filter(Boolean).join(' ')
  const scheduleCalendarStart = buildScheduleCalendarStartDateTime(
    selectedDate,
    timezone,
  )
  const fullAvailabilityHref = buildViewFullAvailabilityScheduleUrl({
    scheduleUrl,
    lessonTypeId: selectedOffering?.lessonTypeId,
    locationId: activeLocationId || selectedOffering?.facilityId,
    selectedDay: hasBookableTimes ? selectedDate : undefined,
    hasLessonPacks: selectedOffering?.hasLessonPacks,
  })

  const trackBookingClick = (buttonName, href, slot) => {
    const event = buttonName === VIEW_FULL_AVAILABILITY_LABEL
      ? 'click-view-full-availability'
      : 'click-book-private-lesson-slot'
    const attributes = {
      coach_name: coachName || null,
      coach_profile_id: coach?.coachProfileId || null,
      lesson_type_id: selectedOffering?.lessonTypeId || null,
      offering_title: selectedOffering?.title || null,
      start_date_time: slot?.startDateTime || null,
      button_name: buttonName,
      href,
    }
    gtmEvent({ event, attributes })
    trackCoachProfileEvent(event === 'click-view-full-availability'
      ? 'click-coach-profile-view-full-availability'
      : 'click-coach-profile-booking-slot', {
      ...attributes,
      location_id: activeLocationId || selectedOffering?.facilityId || null,
      selected_date: selectedDate || null,
      preferred_minutes: preferredMinutes,
    })
  }

  const trackFieldChange = (field, value, offering = selectedOffering) => {
    trackCoachProfileEvent('change-coach-profile-booking-widget-field', {
      field,
      value,
      lesson_type_id: offering?.lessonTypeId || null,
      offering_title: offering?.title || null,
    })
  }

  const showTimesEmptyState = !showAvailabilityProgress
    && (availabilityError || !hasBookableTimes)

  const fullCalendarItem = (
    <li className={styles.timesListItem}>
      <Button
        className={`${styles.timeButton} ${styles.allTimesButton}`}
        variant="contained"
        href={fullAvailabilityHref}
        onClick={() => trackBookingClick(
          VIEW_FULL_AVAILABILITY_LABEL,
          fullAvailabilityHref,
          hasBookableTimes && selectedDate ? { startDateTime: scheduleCalendarStart } : null,
        )}
        data-gtm-button-name="view-full-availability"
      >
        {FULL_CALENDAR_LABEL}
      </Button>
    </li>
  )

  return (
    <section
      className={sidebar ? `${styles.root} ${styles.rootSidebar}` : styles.root}
      data-testid={BOOKING_WIDGET_TEST_ID}
      aria-labelledby={BOOKING_WIDGET_HEADING_ID}
    >
      <Typography
        id={BOOKING_WIDGET_HEADING_ID}
        className={styles.heading}
        variant="h5"
        component="h3"
      >
        {BOOKING_WIDGET_HEADING}
      </Typography>

      {hasMultipleLocations ? (
        <div
          className={`${styles.field} ${styles.offeringField}${selectionRefreshing ? ` ${styles.fieldRefreshing}` : ''}`}
          aria-label="Location"
        >
          <MapPinned className={styles.fieldIcon} {...COACH_PROFILE_FIELD_ICON_PROPS} />
          <Select
            value={activeLocationId}
            onChange={(event) => {
              trackFieldChange('location', event.target.value)
              setSelectedLocationId(event.target.value)
            }}
            disableUnderline
            className={styles.select}
            classes={{ select: styles.selectInput, icon: styles.selectIcon }}
            MenuProps={MENU_PROPS}
            inputProps={{ 'aria-label': 'Location' }}
            renderValue={(value) => {
              const label = locationOptions.find((option) => option.value === value)?.label
              return renderTruncatedSelectLabel(styles, label)
            }}
          >
            {locationOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      ) : null}

      <div
        className={`${styles.field} ${styles.offeringField}${selectionRefreshing ? ` ${styles.fieldRefreshing}` : ''}`}
        aria-label="Private lesson"
      >
        <BookOpen className={styles.fieldIcon} {...COACH_PROFILE_FIELD_ICON_PROPS} />
        {hasMultipleOfferingsAtLocation ? (
          <Select
            value={getOfferingBookingKey(selectedOffering)}
            onChange={(event) => {
              trackFieldChange(
                'lesson',
                event.target.value,
                offeringsAtLocation.find(
                  (row) => getOfferingBookingKey(row) === event.target.value,
                ),
              )
              setSelectedOfferingKey(event.target.value)
            }}
            disableUnderline
            className={styles.select}
            classes={{ select: styles.selectInput, icon: styles.selectIcon }}
            MenuProps={MENU_PROPS}
            inputProps={{ 'aria-label': 'Private lesson' }}
            renderValue={(value) => {
              const offering = offeringsAtLocation.find(
                (row) => getOfferingBookingKey(row) === value,
              )
              return renderTruncatedSelectLabel(
                styles,
                offering ? formatBookingOfferingLabel(offering) : value,
              )
            }}
          >
            {offeringsAtLocation.map((offering) => (
              <MenuItem
                key={getOfferingBookingKey(offering)}
                value={getOfferingBookingKey(offering)}
              >
                {formatBookingOfferingLabel(offering)}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <div className={styles.offeringLabelWrap}>
            <Typography
              component="p"
              variant="body1"
              className={styles.offeringLabel}
              title={selectedOfferingLabel}
            >
              {selectedOfferingLabel}
            </Typography>
          </div>
        )}
      </div>

      {showAvailabilityProgress ? (
        <LinearProgress
          className={styles.timesLoading}
          color="secondary"
          aria-label={availabilityLoading ? 'Loading available times' : 'Updating available times'}
        />
      ) : null}

      {showTimesEmptyState ? (
        <p className={styles.timesStatus}>{NO_TIMES_LABEL}</p>
      ) : null}

      {hasBookableTimes ? (
        <div className={selectionRefreshing ? styles.timesPanelRefreshing : undefined}>
          <div className={styles.dateTimeRow}>
            <div className={`${styles.field} ${styles.dateTimeField}`}>
              <Calendar className={styles.fieldIcon} {...COACH_PROFILE_FIELD_ICON_PROPS} />
              <Select
                value={selectedDate}
                onChange={(event) => {
                  trackFieldChange('date', event.target.value)
                  setSelectedDate(event.target.value)
                }}
                disableUnderline
                className={styles.select}
                classes={{ select: styles.selectInput, icon: styles.selectIcon }}
                MenuProps={MENU_PROPS}
                inputProps={{ 'aria-label': 'Date' }}
                renderValue={(value) => (
                  <span
                    className={styles.selectValueText}
                    title={formatBookingDateLabel(value, timezone)}
                  >
                    {formatBookingDateShortLabel(value, timezone)}
                  </span>
                )}
              >
                {dateOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className={`${styles.field} ${styles.dateTimeField}`}>
              <Clock className={styles.fieldIcon} {...COACH_PROFILE_FIELD_ICON_PROPS} />
              <Select
                value={String(preferredMinutes)}
                onChange={(event) => {
                  trackFieldChange('preferred_time', Number(event.target.value))
                  setPreferredMinutes(Number(event.target.value))
                }}
                disableUnderline
                className={styles.select}
                classes={{ select: styles.selectInput, icon: styles.selectIcon }}
                MenuProps={MENU_PROPS}
                inputProps={{ 'aria-label': 'Preferred time' }}
              >
                {PREFERRED_TIME_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          <Typography className={styles.timesHeading} variant="subtitle1" component="h4">
            {SELECT_A_TIME_LABEL}
          </Typography>

          {nearbySlots.length > 0 ? (
            <ul className={sidebar ? `${styles.timesList} ${styles.timesListSidebar}` : styles.timesList}>
              {nearbySlots.map((slot) => {
                const href = buildPrivateLessonScheduleUrl({
                  scheduleUrl,
                  lessonTypeId: selectedOffering.lessonTypeId,
                  locationId: slot.locationId || activeLocationId || selectedOffering.facilityId,
                  startDateTime: slot.startDateTime,
                  hasLessonPacks: selectedOffering.hasLessonPacks,
                })
                const label = formatSlotTimeLabel(slot.startDateTime, timezone)
                return (
                  <li
                    key={`${slot.startDateTime}-${slot.locationId || ''}`}
                    className={styles.timesListItem}
                  >
                    <Button
                      className={styles.timeButton}
                      variant="contained"
                      href={href}
                      onClick={() => trackBookingClick(label, href, slot)}
                      data-gtm-button-name="book-private-lesson-slot"
                    >
                      {label}
                    </Button>
                  </li>
                )
              })}
              {fullCalendarItem}
            </ul>
          ) : (
            <>
              {showTimesEmptyState ? null : (
                <p className={styles.timesStatus}>{NO_TIMES_LABEL}</p>
              )}
              <ul className={`${styles.timesList} ${styles.timesListSolo}`}>{fullCalendarItem}</ul>
            </>
          )}
        </div>
      ) : null}

      {!hasBookableTimes && !showAvailabilityProgress ? (
        <ul className={`${styles.timesList} ${styles.timesListSolo}`}>{fullCalendarItem}</ul>
      ) : null}
    </section>
  )
}

CoachProfileBookingWidget.propTypes = {
  /** Narrow 1/3-column layout on the flagged profile (md+ sidebar). */
  sidebar: PropTypes.bool,
  coach: PropTypes.shape({
    bookingUrl: PropTypes.string,
    bookingURL: PropTypes.string,
    customSlug: PropTypes.string,
    coachProfileId: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    displayName: PropTypes.string,
  }),
  offerings: PropTypes.arrayOf(PropTypes.shape({
    format: PropTypes.string,
    title: PropTypes.string,
    lessonTypeId: PropTypes.string,
    facilityId: PropTypes.string,
    timezone: PropTypes.string,
    requiresAccessCode: PropTypes.bool,
    hasAvailability: PropTypes.bool,
    hasLessonPacks: PropTypes.bool,
    objectID: PropTypes.string,
    availabilityWindows: PropTypes.arrayOf(PropTypes.string),
    nextAvailableAt: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.string,
    ]),
  })),
}

CoachProfileBookingWidget.defaultProps = {
  coach: {},
  offerings: [],
}

export default CoachProfileBookingWidget
